import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import { Car, Plus, Search, Trash2, Wrench } from 'lucide-react';
import { confirmAction, showError, showSuccess } from '@/utils/alerts.js';
import { Card, CardContent } from '@/components/ui/card.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import AdminPageHeader from '../ui/AdminPageHeader.jsx';
import { M_DELETE_VEHICULO, Q_VEHICULOS } from './vehiculos.gql.js';

const PAGE_SIZE = 10;

export default function VehiculosList() {
  const [searchValue, setSearchValue] = useState('');
  const [variables, setVariables] = useState({ q: undefined, page: 1, limit: PAGE_SIZE });
  const [{ data, fetching, error }, reexecuteQuery] = useQuery({ query: Q_VEHICULOS, variables });
  const [, deleteVehiculo] = useMutation(M_DELETE_VEHICULO);
  const [deletingId, setDeletingId] = useState(null);

  const vehiculos = data?.vehiculos.items ?? [];
  const pageInfo = data?.vehiculos.pageInfo ?? { page: 1, limit: PAGE_SIZE, total: 0 };
  const totalPages = useMemo(() => {
    if (!pageInfo?.limit) {
      return 1;
    }
    return Math.max(1, Math.ceil(pageInfo.total / pageInfo.limit));
  }, [pageInfo]);

  const handleSearchSubmit = event => {
    event.preventDefault();
    const normalized = searchValue.trim();
    setVariables(prev => ({ ...prev, q: normalized.length > 0 ? normalized : undefined, page: 1 }));
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setVariables(prev => ({ ...prev, q: undefined, page: 1 }));
  };

  const handlePageChange = direction => {
    setVariables(prev => {
      const nextPage = direction === 'next' ? prev.page + 1 : prev.page - 1;
      const clamped = Math.min(Math.max(nextPage, 1), totalPages);
      if (clamped === prev.page) {
        return prev;
      }
      return { ...prev, page: clamped };
    });
  };

  const handleDelete = async id => {
    // Mensaje Swal unificado con tono profesional.
    const confirmation = await confirmAction({
      title: 'Confirmar eliminación',
      text: 'El vehículo se eliminará de forma permanente. Esta acción no se puede deshacer.',
      confirmText: 'Eliminar vehículo',
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    try {
      setDeletingId(id);
      const result = await deleteVehiculo({ id });
      if (result.error) {
        await showError('No pudimos eliminar el vehículo', result.error.message);
        return;
      }
      if (!result.data?.deleteVehiculo) {
        await showError('No pudimos eliminar el vehículo', 'No se recibió confirmación del servidor.');
        return;
      }

      await showSuccess('Vehículo eliminado', 'El vehículo fue eliminado correctamente.');

      if (vehiculos.length <= 1 && variables.page > 1) {
        setVariables(prev => ({ ...prev, page: prev.page - 1 }));
      } else {
        reexecuteQuery({ requestPolicy: 'network-only' });
      }
    } catch (mutationError) {
      console.error(mutationError);
      await showError('Ocurrió un error', 'No pudimos completar la eliminación. Intentá nuevamente.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <AdminPageHeader
        title="Vehículos"
        breadcrumbs={[
          { label: 'Administración', href: '/admin' },
          { label: 'Vehículos' },
        ]}
        description="Gestión de la flota con búsqueda, detalle y altas."
        action={
          <Button asChild>
            <Link to="/admin/vehiculos/nuevo">
              <Plus size={16} />
              Nuevo vehículo
            </Link>
          </Button>
        }
      />

      <Card>
        <CardContent className="space-y-4">
          <form onSubmit={handleSearchSubmit} className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
            <div className="relative flex-1">
              <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchValue}
                onChange={event => setSearchValue(event.target.value)}
                placeholder="Buscar por patente, marca o modelo"
                className="pl-9"
                aria-label="Buscar vehículos"
              />
            </div>
            <div className="flex gap-2 self-start md:self-auto">
              <Button type="submit" disabled={fetching}>
                Buscar
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={handleClearSearch}
                disabled={fetching || (searchValue === '' && !variables.q)}
              >
                Limpiar
              </Button>
            </div>
          </form>

          {error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300">
              <p className="font-semibold">No se pudo cargar la lista de vehículos.</p>
              <p className="mt-1">{error.message}</p>
              <Button className="mt-3" variant="outline" onClick={() => reexecuteQuery({ requestPolicy: 'network-only' })}>
                Reintentar
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto rounded-xl border border-slate-200/70 dark:border-slate-800/70">
                <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                  <thead className="bg-slate-50/80 dark:bg-slate-900/60">
                    <tr className="text-left text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      <th className="px-4 py-3">Patente</th>
                      <th className="px-4 py-3">Marca</th>
                      <th className="px-4 py-3">Modelo</th>
                      <th className="px-4 py-3">Cliente</th>
                      <th className="px-4 py-3">Estado</th>
                      <th className="px-4 py-3 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200/70 text-sm dark:divide-slate-800/60">
                    {fetching && vehiculos.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-6 text-center text-slate-500 dark:text-slate-400">
                          Cargando listado de vehículos...
                        </td>
                      </tr>
                    ) : vehiculos.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-6 text-center text-slate-500 dark:text-slate-400">
                          No hay vehículos para mostrar. Probá limpiar filtros o crear uno nuevo.
                        </td>
                      </tr>
                    ) : (
                      vehiculos.map(vehiculo => (
                        <tr key={vehiculo.id} className="bg-white/70 transition hover:bg-slate-50 dark:bg-slate-900/40 dark:hover:bg-slate-800/40">
                          <td className="px-4 py-3 font-semibold text-slate-900 dark:text-slate-100">{vehiculo.patente}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{vehiculo.marca || '—'}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{vehiculo.modelo || '—'}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                            {vehiculo.cliente?.nombre || (vehiculo.cliente_id ? `ID ${vehiculo.cliente_id}` : 'Sin asignar')}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                                vehiculo.estado === 'ACTIVO'
                                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300'
                                  : 'bg-slate-200 text-slate-600 dark:bg-slate-700/60 dark:text-slate-300'
                              }`}
                            >
                              {vehiculo.estado}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-2">
                              <Button asChild variant="ghost" size="sm">
                                <Link to={`/administracion/vehiculos/${vehiculo.id}`} className="flex items-center gap-1">
                                  <Car size={16} />
                                  Ver
                                </Link>
                              </Button>
                              <Button asChild variant="ghost" size="sm">
                                <Link to={`/administracion/vehiculos/${vehiculo.id}/editar`} className="flex items-center gap-1">
                                  <Wrench size={16} />
                                  Editar
                                </Link>
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDelete(vehiculo.id)}
                                disabled={deletingId === vehiculo.id}
                              >
                                <Trash2 size={16} />
                                Eliminar
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col gap-3 text-sm text-slate-600 dark:text-slate-300 md:flex-row md:items-center md:justify-between">
                <span>
                  Página {pageInfo.page} de {totalPages} — {pageInfo.total} vehículos
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange('prev')}
                    disabled={pageInfo.page <= 1 || fetching}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange('next')}
                    disabled={pageInfo.page >= totalPages || fetching}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
